import { ReactNode, useEffect, useState } from "react";
import {
  AlertColor,
  Box,
  Button,
  LinearProgress,
  Typography,
} from "@mui/material";
import moment from "moment-timezone";
import { useUsuario } from "../../../providers/usuarioProvider";
import { Alert, AlertTitle } from "@mui/material";
import {
  IconCalculator,
  IconRefreshAlert,
  IconTrophy,
  IconConfetti,
  IconAlertCircle,
  Icon,
  IconRefresh,
  IconFile,
  IconFlag,
} from "@tabler/icons-react";
import felixAnalytics from "../../../felixAnalytics";
import {
  MotivosAtualizar,
  Perfil,
  SituacaoCalculo,
} from "../../../models/enum";
import Usuario from "../../../models/usuario";
import { split } from "lodash";
import { addDoc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import UsuarioDb from "../../../db/usuarioDb";
import CalculoDb from "../../../db/calculoDb";
import Calculo from "../../../models/calculo";
import { Link } from "react-router-dom";
import { Crisp } from "crisp-sdk-web";
import AlertaVendaSemCompra from "./AlertaVendaSemCompra";
import AlertaExercicioOpcao from "./AlertaExercicioOpcao";
import AlertaExercicioSubscricao from "./AlertaExercicioSubscricao";
import { radius } from "../../../theme";

/** Data/valor em mono tabular, no tamanho do texto ao redor. */
const dadoInline = {
  fontSize: "inherit",
  fontWeight: 500,
  color: "text.primary",
} as const;

/** Severidade do MUI → tint semântico do sistema (DESIGN.md → Chips de status). */
const TINT: Record<
  AlertColor,
  "positive" | "accent" | "warn" | "destructive"
> = {
  success: "positive",
  info: "accent",
  warning: "warn",
  error: "destructive",
};

type SituacaoCalculoView = {
  titulo: string;
  severidade: AlertColor;
  Icone: Icon;
  /** Ações como LISTA: cada casca decide o arranjo — o Alert empilha na lateral,
   *  o painel alinha em linha no rodapé. */
  acoes: ReactNode[];
  conteudo: ReactNode;
};

/**
 * Estado do cálculo do usuário, já traduzido para o que a tela mostra.
 *
 * Era o corpo do `Atualizar`. Virou hook para que a mesma lógica sirva a duas
 * apresentações: o `Alert` (usado em Inteligência, Internacional e no vazio da
 * Timeline) e o `PainelSituacaoCalculo` da primeira row do mês.
 */
export function useSituacaoCalculo(): SituacaoCalculoView {
  const { visualizado, claims } = useUsuario();
  // Quem lê é o contador, não o investidor: textos sobre o cliente em 3ª pessoa.
  const leitorContador = claims?.perfil === Perfil.CONTADOR;
  const [ultimoCalculo, setUltimoCalculo] = useState<Calculo>();
  const [usuario, setUsuario] = useState<Usuario>();
  // `undefined` em usuario/ultimoCalculo é ambíguo (ainda carregando OU não
  // existe). Sem distinguir, o hook decidia o estado com dados pela metade: o
  // snapshot do usuário chega antes do de cálculos e o painel piscava um
  // "Calcular" funcional que sumia quando o cálculo com erro chegava.
  const [usuarioCarregado, setUsuarioCarregado] = useState(false);
  const [calculoCarregado, setCalculoCarregado] = useState(false);

  useEffect(() => {
    if (visualizado) {
      setUsuarioCarregado(false);
      return onSnapshot(
        new UsuarioDb().doc({ usuarioId: visualizado }),
        (snapshot) => {
          setUsuario(snapshot.data());
          setUsuarioCarregado(true);
        },
      );
    }
  }, [visualizado]);

  useEffect(() => {
    if (visualizado) {
      setCalculoCarregado(false);
      return onSnapshot(
        query(
          new CalculoDb().collection({ usuarioId: visualizado, calculoId: "" }),
          orderBy("dataCriacao", "desc"),
          limit(1),
        ),
        (snapshot) => {
          setUltimoCalculo(snapshot.empty ? undefined : snapshot.docs[0].data());
          setCalculoCarregado(true);
        },
      );
    }
  }, [visualizado]);

  function onCalcular() {
    if (usuario)
      addDoc(
        new CalculoDb().collection({ usuarioId: usuario.id, calculoId: "" }),
        new Calculo({
          id: "",
          dataCalculo: moment.tz("America/Sao_Paulo"),
          dataCriacao: moment.tz("America/Sao_Paulo"),
          dataFim: moment.tz("America/Sao_Paulo").endOf("day"),
          dataInicio: usuario.atualizadoAte,
          dataProgresso: usuario.atualizadoAte,
          situacao: SituacaoCalculo.CALCULANDO,
        }),
      ).finally(() => {
        felixAnalytics.identify(usuario.id);
        felixAnalytics.track("calcular");
      });
  }

  let titulo: string;
  let severidade: AlertColor;
  let Icone: Icon;
  let acoes: ReactNode[] = [];
  let conteudo: ReactNode;

  const novo = !usuario?.estatisticas;
  const hoje = moment.tz("America/Sao_Paulo");

  if (!usuarioCarregado || !calculoCarregado) {
    // CARREGANDO: sem ações até os DOIS snapshots chegarem — decidir o estado
    // com um só deles renderizava botões de um estado que não era o real.
    titulo = "Carregando...";
    severidade = "info";
    Icone = IconRefresh;
    conteudo = (
      <Box width="100%" py={2}>
        <LinearProgress
          sx={{
            height: 8,
            borderRadius: `${radius.pill}px`,
          }}
          color="info"
        />
      </Box>
    );
  } else if (novo) {
    severidade = "info";
    Icone = IconConfetti;
    if (leitorContador) {
      titulo = `${split(usuario?.nome, " ", 1)} ainda não tem notas cadastradas.`;
      conteudo = (
        <Typography variant="body2">
          Importe as notas de corretagem ou o extrato da B3 do cliente para
          iniciar a apuração.
        </Typography>
      );
    } else {
      titulo = `Você é novo(a) por aqui, ${split(usuario?.nome, " ", 1)}?`;
      conteudo = (
        <Typography variant="body2">
          Você está a poucos segundos de passar a ter confiança ao investir na
          Bolsa.
        </Typography>
      );
    }
  } else if (ultimoCalculo?.situacao === SituacaoCalculo.CALCULANDO) {
    titulo = leitorContador
      ? "Aguarde enquanto os cálculos do cliente são atualizados..."
      : "Aguarde enquanto seus cálculos estão sendo atualizados...";
    severidade = "info";
    Icone = IconRefresh;
    conteudo = (
      <Box width="100%" py={2}>
        <LinearProgress
          sx={{
            height: 8,
            borderRadius: `${radius.pill}px`,
          }}
          color="info"
          variant="determinate"
          value={
            (ultimoCalculo.dataProgresso.diff(ultimoCalculo.dataInicio, "day") *
              100) /
            ultimoCalculo.dataFim.diff(ultimoCalculo.dataInicio, "day")
          }
        />
      </Box>
    );
  } else if (
    ultimoCalculo?.situacao === SituacaoCalculo.ERRO &&
    usuario?.atualizadoAte?.isSame(ultimoCalculo.dataProgresso, "day") &&
    (usuario?.atualizarPorque ?? "-") === "-"
  ) {
    // ERRO: mostra apenas se atualizadoAte == dataProgresso (em dias) e nada
    // mudou desde o cálculo que falhou (atualizarPorque == "-"). Se o usuário
    // mexeu em qualquer dado depois do erro (ex: excluiu o exercício que
    // causou a falha), o erro está potencialmente obsoleto — cai no ramo
    // DESATUALIZADO, que tem o botão Calcular.
    Icone = IconRefreshAlert;
    severidade = "warning";
    switch (ultimoCalculo.erro?.codErro) {
      case 2: {
        titulo = "Atenção, alguma nota não foi lida corretamente.";
        conteudo = (
          <Typography variant="body2">
            {leitorContador
              ? "Confira as notas do cliente na tela de Arquivo."
              : "Dê uma olhada nas suas notas na tela de Arquivo."}
          </Typography>
        );
        acoes = [
          <Button
            key="ver-notas"
            variant="contained"
            color="primary"
            size="medium"
            component={Link}
            to="/arquivo#uploads"
            startIcon={<IconFile />}
          >
            Ver Notas
          </Button>,
          <Button
            key="recalcular"
            variant="outlined"
            size="small"
            color="inherit"
            onClick={onCalcular}
          >
            Recalcular
          </Button>,
        ];
        break;
      }
      case 3:
      case 22: {
        const operacao = ultimoCalculo.erro.lancamento.lancamento;
        titulo = `Venda sem compra de ${operacao.codigoAtivo}`;
        conteudo = (
          <AlertaVendaSemCompra
            codigoAtivo={operacao.codigoAtivo}
            quantidade={operacao.quantidade}
            dataPregao={operacao.dataPregao}
          />
        );
        break;
      }
      case 12: {
        const operacao = ultimoCalculo.erro.lancamento.lancamento;
        titulo = `Exercício de opção de ${operacao.codigoAtivo}`;
        conteudo = (
          <AlertaExercicioOpcao
            codigoAtivo={operacao.codigoAtivo}
            quantidade={operacao.quantidade}
            dataPregao={operacao.dataPregao}
          />
        );
        break;
      }
      case 25: {
        const operacao = ultimoCalculo.erro.lancamento.lancamento;
        titulo = `Exercício de subscrição de ${operacao.codigoAtivo}`;
        conteudo = (
          <AlertaExercicioSubscricao
            codigoAtivo={operacao.codigoAtivo}
            dataExercicio={operacao.dataExercicio}
          />
        );
        break;
      }
      case 46: {
        titulo = "Uma operação internacional precisa da sua atenção.";
        const operacao = ultimoCalculo.erro.operacao;
        // Causa real propagada pelo backend; cálculos antigos não têm o campo
        const causa: string = (
          ultimoCalculo.erro.detalhes ??
          "esse ativo não foi encontrado na sua custódia internacional"
        ).replace(/\.\s*$/, "");
        conteudo = (
          <>
            <Typography variant="body2">
              No dia{" "}
              <Typography component="span" variant="dataMono" sx={dadoInline}>
                {moment(operacao.dataPregao).format("DD/MM/YYYY")}
              </Typography>
              {` houve uma ${
                operacao.tipoOperacao === "SELL" ? "venda" : "operação"
              }${
                operacao.ativo ? ` de ${operacao.ativo}` : ""
              } (${operacao.moeda}) que não pôde ser processada: ${causa}.`}
            </Typography>
            <Typography component="ol" sx={{ mt: 1, pl: 3 }}>
              <Typography variant="body2" component="li">
                Verifique se você cadastrou o ponto de partida internacional com
                a custódia inicial.
              </Typography>
              <Typography variant="body2" component="li">
                Verifique se todos os statements foram importados corretamente.
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Após corrigir, clique em recalcular.
            </Typography>
          </>
        );
        acoes = [
          <Button
            key="ponto-de-partida"
            variant="outlined"
            color="primary"
            size="medium"
            component={Link}
            to="/internacional#ponto-de-partida"
            startIcon={<IconFlag />}
          >
            Ponto de Partida Internacional
          </Button>,
          // Botão `text` é tinta, não preenchimento: `color="primary"`
          // pintaria o rótulo em `primary.main` (2,57:1 sobre surface no
          // dark). `info` é o canal do accent-ink no tema.
          <Button
            key="recalcular"
            variant="text"
            size="small"
            color="info"
            onClick={onCalcular}
          >
            Recalcular
          </Button>,
        ];
        break;
      }
      default: {
        titulo = "Ops, deu algo de errado no seu cálculo.";
        severidade = "error";
        acoes = [
          <Button
            key="calcular"
            variant="contained"
            size="medium"
            color="primary"
            onClick={onCalcular}
            startIcon={<IconAlertCircle />}
          >
            Calcular
          </Button>,
        ];
        conteudo =
          "Mas não se preocupe. Envie um email para suporte@felixnabolsa.com.br que vamos te ajudar.";
        Crisp.chat.open();
        break;
      }
    }
    // `component="div"`: o conteúdo pode ser um bloco (Box/Typography), e
    // <p> não pode conter <div>.
    conteudo = (
      <Typography variant="body2" component="div">
        {conteudo}
      </Typography>
    );
  } else if (
    ultimoCalculo?.situacao === SituacaoCalculo.PRONTO &&
    usuario?.atualizadoAte?.isSameOrAfter(hoje, "day")
  ) {
    // EM DIA: PRONTO e atualizadoAte >= hoje
    titulo = "Tudo atualizado por aqui.";
    severidade = "success";
    Icone = IconTrophy;
    conteudo = (
      <Typography variant="body2">
        {leitorContador
          ? "Os cálculos do cliente estão em dia"
          : "Os seus cálculos estão em dia"}
        {!!usuario.ultimaNota ? (
          <>
            {leitorContador
              ? "; a última operação foi em "
              : ", sendo a última vez que operou em "}
            <Typography component="span" variant="dataMono" sx={dadoInline}>
              {usuario.ultimaNota.format("DD/MM/YYYY")}
            </Typography>
            .
          </>
        ) : (
          "."
        )}
      </Typography>
    );
  } else {
    // DESATUALIZADO: todos os outros casos
    titulo = "Opa, tem coisa nova na área!";
    severidade = "warning";
    Icone = IconRefreshAlert;
    acoes = [
      <Button
        key="calcular"
        variant="contained"
        size="medium"
        color="primary"
        onClick={onCalcular}
        startIcon={<IconCalculator />}
      >
        Calcular
      </Button>,
    ];

    // Motivo por extenso. Record completo: motivo novo no enum quebra o build
    // aqui em vez de cair calado na frase genérica.
    const motivos: Record<
      MotivosAtualizar,
      { investidor: string; contador: string }
    > = {
      [MotivosAtualizar.NOTAS_APAGADAS]: {
        investidor: "apagou algumas notas",
        contador: "notas foram apagadas",
      },
      [MotivosAtualizar.NOTAS_CADASTRADAS]: {
        investidor: "cadastrou algumas notas",
        contador: "notas foram cadastradas",
      },
      [MotivosAtualizar.POSICAO_CADASTRADA]: {
        investidor: "editou seu Ponto de partida",
        contador: "o Ponto de partida foi editado",
      },
      [MotivosAtualizar.EVENTOS_CADASTRADOS]: {
        investidor: "eventos corporativos foram cadastrados",
        contador: "eventos corporativos foram cadastrados",
      },
      [MotivosAtualizar.EXERCICIOS_CADASTRADOS]: {
        investidor: "seus exercícios de subscrição mudaram",
        contador: "os exercícios de subscrição mudaram",
      },
    };
    const motivo =
      usuario?.atualizarPorque && usuario.atualizarPorque !== "-"
        ? motivos[usuario.atualizarPorque]?.[
            leitorContador ? "contador" : "investidor"
          ]
        : undefined;
    conteudo = (
      <Typography variant="body2">
        {leitorContador
          ? "Os cálculos do cliente estão desatualizados desde "
          : "Os seus cálculos estão desatualizados desde "}
        <Typography component="span" variant="dataMono" sx={dadoInline}>
          {usuario?.atualizadoAte.format("DD/MM/YYYY")}
        </Typography>
        {motivo ? ` porque ${motivo}.` : "."}
      </Typography>
    );
  }

  return { titulo, severidade, Icone, acoes, conteudo };
}

/**
 * Apresentação em Alert — Inteligência, Internacional e Timeline vazia.
 * Na primeira row do mês use `PainelSituacaoCalculo`.
 */
export default function Atualizar() {
  const { titulo, severidade, Icone, acoes, conteudo } = useSituacaoCalculo();

  return (
    <Alert
      sx={{
        p: 2,
        // Hierarquia por hairline, não por traço grosso (DESIGN.md → Elevation).
        borderWidth: 1,
        borderRadius: `${radius.lg}px`,
        height: "100%",
        // O card estica para acompanhar a altura do vizinho, e o Alert do MUI
        // alinha ícone/mensagem/ação no TOPO — todo o vazio sobrava embaixo.
        // Centralizando, o espaço se distribui e o card fica cheio.
        alignItems: "center",
        "& .MuiAlert-icon": { alignSelf: "center", py: 0, mt: 0 },
        "& .MuiAlert-action": { alignSelf: "center", py: 0, mt: 0 },
        "& .MuiAlertTitle-root": { fontWeight: 600 },
        "&.MuiAlert-filled .MuiAlert-icon": {
          color: `${severidade}.contrastText`,
        },
        "&.MuiAlert-filled .MuiAlert-message": {
          color: `${severidade}.contrastText`,
        },
        "& .MuiAlert-message": {
          width: "100%",
          py: 0,
        },
      }}
      variant="outlined"
      severity={severidade}
      icon={<Icone size={24} />}
      action={
        acoes.length > 0 ? (
          <Box display="flex" flexDirection="column" gap={1}>
            {acoes}
          </Box>
        ) : undefined
      }
    >
      <AlertTitle>{titulo}</AlertTitle>
      {conteudo}
    </Alert>
  );
}

type PainelProps = {
  /** Ações da TELA (Ver Detalhes, Analisar com IA) — ficam no topo, à direita. */
  acoesDaTela?: ReactNode;
};

/**
 * Apresentação em painel, para a primeira row do mês.
 *
 * A mensagem do cálculo é o conteúdo mais importante da row — principalmente
 * quando é erro, que traz parágrafo e até três ações. Por isso ela mora num
 * bloco que simplesmente CRESCE, sem regra de exceção: o filete semântico à
 * esquerda dá a severidade, e as ações do erro caem no rodapé, alinhadas à
 * direita.
 */
export function PainelSituacaoCalculo({ acoesDaTela }: PainelProps) {
  const { titulo, severidade, Icone, acoes, conteudo } = useSituacaoCalculo();
  const tint = TINT[severidade];

  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
        p: 2.5,
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${theme.palette.statusChip[tint].fg}`,
        borderRadius: `${radius.lg}px`,
      })}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <Typography variant="labelMono" color="text.secondary">
          Situação do cálculo
        </Typography>
        {acoesDaTela}
      </Box>

      <Box display="flex" alignItems="flex-start" gap={1.75} flexGrow={1}>
        <Box
          sx={(theme) => ({
            display: "flex",
            flex: "none",
            pt: 0.25,
            color: theme.palette.statusChip[tint].fg,
          })}
        >
          <Icone size={28} />
        </Box>
        <Box minWidth={0}>
          <Typography
            variant="h4"
            component="p"
            color="text.primary"
            gutterBottom
          >
            {titulo}
          </Typography>
          {conteudo}
        </Box>
      </Box>

      {acoes.length > 0 && (
        <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
          {acoes}
        </Box>
      )}
    </Box>
  );
}
